import { useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { api } from "@/lib/api";
import { localizeApiError } from "@/lib/apiErrors";
import { useI18n } from "@/context/I18nContext";
import { CATEGORIES, categoryColor } from "@/lib/categories";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ScanLine, Loader2, Check, RefreshCw, AlertTriangle, Info, ShieldCheck, X } from "lucide-react";
import { ReceiptSourceChooser } from "@/components/ReceiptSourceChooser";
import { ScanEffect } from "@/components/ScanEffect";
import { prepareImageForUpload } from "@/lib/imagePrep";

const CURRENCIES = ["PLN", "EUR", "USD", "GBP", "JPY", "INR"];
const HIGH_AMOUNT_THRESHOLD = 1000; // PLN: warn above this to catch barcode/ID misreads

// Normalize a merchant string for loose comparison (duplicate detection).
function normMerchant(m) {
  return (m || "").trim().toLowerCase().replace(/\s+/g, " ");
}

// Non-blocking probable-duplicate check against the user's existing receipts:
// same merchant (loose) + same date + amount within 1 grosz.
function findDuplicate(form, list) {
  if (!Array.isArray(list) || !form) return false;
  const m = normMerchant(form.merchant);
  const amt = Number(form.total_amount) || 0;
  if (!m || !form.date || amt <= 0) return false;
  return list.some(
    (r) =>
      normMerchant(r.merchant) === m &&
      (r.date || "") === form.date &&
      Math.abs((Number(r.total_amount) || 0) - amt) < 0.01
  );
}

// SubZero Verify (Beta): lightweight integrity/consistency checks derived from
// data already produced by the scan. NOT an authenticity/forensic detector.
function buildVerifyChecks(form, t) {
  const merchantOk = !!(form.merchant && form.merchant.trim());
  const amt = Number(form.total_amount) || 0;
  const amountOk = amt > 0;
  const dateStr = form.date || "";
  const dateOk = !!dateStr;
  let futureBad = false;
  let oldWarn = false;
  if (dateOk) {
    const d = new Date(dateStr + "T00:00:00");
    if (!isNaN(d)) {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const diffDays = Math.round((d - today) / 86400000);
      futureBad = diffDays > 2;
      oldWarn = d.getFullYear() < now.getFullYear() - 6;
    }
  }
  const confident = !form.date_uncertain;
  const consistent = merchantOk && amountOk && dateOk && !futureBad;
  return [
    { key: "merchant", status: merchantOk ? "ok" : "warn" },
    { key: "amount", status: amountOk ? "ok" : "warn" },
    { key: "date", status: dateOk ? "ok" : "warn" },
    { key: "future", status: futureBad ? "bad" : "ok" },
    { key: "old", status: oldWarn ? "warn" : "ok", labelKey: oldWarn ? "verify.check.oldWarn" : "verify.check.old" },
    { key: "confidence", status: confident ? "ok" : "warn" },
    { key: "consistency", status: consistent ? "ok" : futureBad ? "bad" : "warn" },
  ];
}

function SubZeroVerify({ form, duplicate, t }) {
  const checks = buildVerifyChecks(form, t);
  const hasBad = checks.some((c) => c.status === "bad");
  const hasWarn = checks.some((c) => c.status === "warn") || duplicate;
  const overall = hasBad ? "bad" : hasWarn ? "warn" : "ok";

  const palette = {
    ok: { bg: "var(--success-bg)", fg: "var(--success-fg)", bd: "var(--success-border)", Icon: Check },
    warn: { bg: "var(--warning-bg)", fg: "var(--warning-fg)", bd: "var(--warning-border)", Icon: AlertTriangle },
    bad: { bg: "var(--danger-bg)", fg: "var(--danger-fg)", bd: "var(--danger-border)", Icon: X },
  };
  const head = palette[overall];
  const statusText = overall === "ok" ? t("verify.green") : overall === "warn" ? t("verify.yellow") : t("verify.red");

  return (
    <div className="mt-4 rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid="subzero-verify-card">
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck className="text-cyan-300" style={{ width: 18, height: 18 }} />
        <h4 className="font-display font-semibold text-sm">{t("verify.title")}</h4>
        <span className="text-[10px] uppercase tracking-wide rounded-full px-2 py-0.5 bg-cyan-400/15 text-cyan-200 border border-cyan-400/25">Beta</span>
      </div>

      <div
        data-testid="subzero-verify-status"
        data-status={overall}
        className="flex items-start gap-2.5 rounded-lg px-3 py-2.5 text-sm font-medium mb-3"
        style={{ background: head.bg, color: head.fg, border: `1px solid ${head.bd}` }}
      >
        <head.Icon style={{ width: 16, height: 16, marginTop: 1, flexShrink: 0 }} />
        <span>{statusText}</span>
      </div>

      <ul className="space-y-1.5 mb-3">
        {checks.map((c) => {
          const p = palette[c.status];
          return (
            <li key={c.key} className="flex items-center gap-2 text-xs" style={{ color: c.status === "ok" ? "var(--muted-foreground, rgba(255,255,255,0.65))" : p.fg }}>
              <p.Icon style={{ width: 13, height: 13, flexShrink: 0 }} />
              <span>{t(c.labelKey || `verify.check.${c.key}`)}</span>
            </li>
          );
        })}
      </ul>

      {duplicate && (
        <div className="flex items-start gap-2 text-xs mb-3 rounded-lg px-3 py-2" style={{ background: "var(--warning-bg)", color: "var(--warning-fg)", border: "1px solid var(--warning-border)" }} data-testid="subzero-verify-duplicate">
          <Info style={{ width: 13, height: 13, marginTop: 1, flexShrink: 0 }} />
          <span>{t("warn.duplicate")}</span>
        </div>
      )}

      <p className="text-[11px] leading-relaxed text-muted-foreground">{t("verify.disclaimer")}</p>
    </div>
  );
}


function computeWarnings(form, t, duplicate) {
  const warnings = [];
  const merchantMissing = !form.merchant || !form.merchant.trim();
  const dateMissing = !form.date;

  if (merchantMissing && dateMissing) {
    warnings.push({ level: "error", text: t("warn.merchantAndDateMissing") });
  } else {
    if (merchantMissing) warnings.push({ level: "error", text: t("warn.merchantMissing") });
    if (dateMissing) warnings.push({ level: "warn", text: t("warn.dateMissing") });
  }

  if (!dateMissing) {
    const d = new Date(form.date + "T00:00:00");
    if (!isNaN(d)) {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const diffDays = Math.round((d - today) / 86400000);
      // A receipt counts toward "This month" when it is in the SAME year+month as now.
      const sameMonth =
        d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
      if (diffDays > 1) {
        warnings.push({ level: "warn", text: t("warn.dateFuture") });
      } else if (!sameMonth) {
        // Only warn when the receipt belongs to a DIFFERENT (past) month/year,
        // i.e. it genuinely won't count toward the current month total.
        warnings.push({ level: "info", text: t("warn.dateOld") });
      }
    }
    // The AI was not fully confident about the extracted date -> ask the user to confirm it.
    if (form.date_uncertain) {
      warnings.push({ level: "warn", text: t("warn.dateConfirm") });
    }
  }

  const amt = Number(form.total_amount) || 0;
  if (amt > HIGH_AMOUNT_THRESHOLD) {
    warnings.push({ level: "warn", text: t("warn.amountHigh") });
  }
  if (duplicate) {
    warnings.push({ level: "warn", text: t("warn.duplicate") });
  }
  return warnings;
}

// The scan effect stays on screen at least this long so the user can perceive it,
// even when the backend answers quickly. Does not delay the API call itself.
const MIN_SCAN_EFFECT_MS = 2800;

export default function ReceiptScannerPage() {
  const navigate = useNavigate();
  const { t, tc } = useI18n();
  const ctx = useOutletContext();
  const [stage, setStage] = useState("idle"); // idle | scanning | review
  const [dragOver, setDragOver] = useState(false);
  const [preview, setPreview] = useState(null);
  const [form, setForm] = useState(null);
  const [confidence, setConfidence] = useState(null);
  const [saving, setSaving] = useState(false);
  const [receiptsList, setReceiptsList] = useState([]);

  const handleFile = async (inputFile) => {
    if (!inputFile) return;
    if (!/^image\/(png|jpe?g|webp)$/i.test(inputFile.type)) {
      toast.error(t("toast.imageType"));
      return;
    }
    setStage("scanning");
    const scanStartedAt = Date.now();
    // Downscale huge phone photos (<= 2000 px, JPEG) before preview + upload. The
    // backend only ever uses a <= 1400 px copy for extraction, so OCR is unaffected;
    // this just avoids multi-MB uploads / 413s and heavy decoding on mobile.
    const file = await prepareImageForUpload(inputFile);
    setPreview((old) => { if (old) { try { URL.revokeObjectURL(old); } catch {} } return URL.createObjectURL(file); });
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.scanReceipt(fd);
      const d = res.data;
      const elapsed = Date.now() - scanStartedAt;
      if (elapsed < MIN_SCAN_EFFECT_MS) {
        await new Promise((resolve) => setTimeout(resolve, MIN_SCAN_EFFECT_MS - elapsed));
      }
      setForm({
        merchant: d.merchant || "",
        total_amount: d.total_amount ?? 0,
        currency: d.currency || "PLN",
        date: d.date || "",
        category: d.category || "Other",
        notes: d.notes || "",
        image_data: d.image_data || null,
        date_uncertain: !!d.date_uncertain,
      });
      setConfidence(d.confidence);
      setStage("review");
      toast.success(t("toast.scanned"));
      // Load existing receipts (best-effort) for the non-blocking duplicate check.
      try {
        const rl = await api.listReceipts();
        setReceiptsList(Array.isArray(rl.data) ? rl.data : []);
      } catch {
        setReceiptsList([]);
      }
    } catch (e) {
      const status = e?.response?.status;
      const detail = localizeApiError(e, t, "toast.scanFailed");
      if (status === 402) {
        toast.error(detail);
        ctx?.openUpgrade?.();
      } else {
        toast.error(detail);
      }
      setStage("idle");
      setPreview(null);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  const save = async () => {
    if (!form.merchant.trim()) { toast.error(t("toast.merchantRequired")); return; }
    setSaving(true);
    try {
      await api.createReceipt({ ...form, total_amount: Number(form.total_amount) || 0, confidence });
      toast.success(t("toast.receiptSaved"));
      navigate("/receipts");
    } catch (e) {
      toast.error(localizeApiError(e, t, "toast.receiptSaveError"));
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    setPreview((old) => { if (old) { try { URL.revokeObjectURL(old); } catch {} } return null; });
    setStage("idle"); setForm(null); setConfidence(null); setReceiptsList([]);
  };

  return (
    <div className="max-w-4xl mx-auto" data-testid="receipt-scanner-page">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-display font-semibold tracking-tight">{t("scan.title")}</h1>
        <p className="text-muted-foreground text-sm mt-1">{t("scan.subtitle")}</p>
      </div>

      {stage === "idle" && (
        <ReceiptSourceChooser
          onFile={handleFile}
          dragOver={dragOver}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
        />
      )}

      {stage === "scanning" && <ScanEffect src={preview} />}

      {stage === "review" && form && (
        <div className="grid lg:grid-cols-2 gap-5">
          <div className="card-base p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-semibold text-sm">{t("scan.receiptImage")}</h3>
              {confidence != null && (
                <span className="text-xs rounded-full px-2.5 py-1 bg-white/5 text-muted-foreground border border-white/10">
                  {t("scan.confidence", { n: Math.round(confidence * 100) })}
                </span>
              )}
            </div>
            <div className="rounded-lg overflow-hidden bg-surface-0 border border-white/10 flex items-center justify-center min-h-[280px]">
              <img src={form.image_data || preview} alt="receipt" className="max-h-[440px] w-auto object-contain" />
            </div>
          </div>

          <div className="card-base p-5">
            <div className="flex items-center gap-2 mb-4 text-cyan-200">
              <ScanLine style={{ width: 18, height: 18 }} />
              <h3 className="font-display font-semibold">{t("scan.reviewSave")}</h3>
            </div>
            {(() => {
              const duplicate = findDuplicate(form, receiptsList);
              const warnings = computeWarnings(form, t, duplicate);
              if (warnings.length === 0) return null;
              const styleFor = (lvl) =>
                lvl === "error"
                  ? { bg: "var(--danger-bg)", fg: "var(--danger-fg)", bd: "var(--danger-border)" }
                  : lvl === "warn"
                  ? { bg: "var(--warning-bg)", fg: "var(--warning-fg)", bd: "var(--warning-border)" }
                  : { bg: "var(--info-bg)", fg: "var(--info-fg)", bd: "var(--info-border)" };
              return (
                <div className="space-y-2 mb-4" data-testid="receipt-warnings">
                  {warnings.map((w, i) => {
                    const s = styleFor(w.level);
                    const Icon = w.level === "info" ? Info : AlertTriangle;
                    return (
                      <div key={i} data-testid={`receipt-warning-${w.level}`} className="flex items-start gap-2.5 rounded-lg px-3 py-2.5 text-sm" style={{ background: s.bg, color: s.fg, border: `1px solid ${s.bd}` }}>
                        <Icon style={{ width: 16, height: 16, marginTop: 1, flexShrink: 0 }} />
                        <span>{w.text}</span>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
            <div className="space-y-4">
              <div>
                <Label className="text-xs text-muted-foreground">{t("field.merchant")}</Label>
                <Input data-testid="receipt-merchant-input" value={form.merchant} onChange={(e) => setForm({ ...form, merchant: e.target.value })} className="mt-1 bg-white/5 border-white/10" placeholder={t("scan.merchantPlaceholder")} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-muted-foreground">{t("field.amount")}</Label>
                  <Input data-testid="receipt-amount-input" type="number" step="0.01" value={form.total_amount} onChange={(e) => setForm({ ...form, total_amount: e.target.value })} className="mt-1 bg-white/5 border-white/10 font-mono" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">{t("field.currency")}</Label>
                  <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                    <SelectTrigger data-testid="receipt-currency-select" className="mt-1 bg-white/5 border-white/10"><SelectValue /></SelectTrigger>
                    <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-muted-foreground">{t("field.date")}</Label>
                  <Input data-testid="receipt-date-input" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1 bg-white/5 border-white/10" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">{t("field.category")}</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="receipt-category-select" className="mt-1 bg-white/5 border-white/10"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => (
                        <SelectItem key={c} value={c}>
                          <span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ background: categoryColor(c) }} />{tc(c)}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">{t("field.notes")}</Label>
                <Textarea data-testid="receipt-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 bg-white/5 border-white/10 resize-none" rows={2} placeholder={t("scan.optional")} />
              </div>
              <div className="flex gap-3 pt-1">
                <Button data-testid="receipt-scan-submit-button" onClick={save} disabled={saving} className="flex-1 bg-cyan-400 text-slate-900 hover:bg-cyan-300 font-semibold">
                  {saving ? <Loader2 className="animate-spin mr-2" style={{ width: 16, height: 16 }} /> : <Check className="mr-2" style={{ width: 16, height: 16 }} />} {t("scan.saveReceipt")}
                </Button>
                <Button variant="outline" onClick={reset} className="border-white/15 bg-white/5 hover:bg-white/10" data-testid="receipt-rescan-button">
                  <RefreshCw style={{ width: 16, height: 16 }} />
                </Button>
              </div>
              <SubZeroVerify form={form} duplicate={findDuplicate(form, receiptsList)} t={t} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
